import 'react-international-phone/style.css';
import { useRef } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FlagImage, usePhoneInput } from 'react-international-phone';

/*
  A phone number as HubSpot's own editor takes it: typed as digits, shown as +1 (785) 554-1335, and
  saved as +17855541335 - the punctuation is display only.

  Not CasRHFPhoneNumberField: that one hides the dial code and saves ten bare digits, which suits
  the app's own US-only numbers but not a value HubSpot stores and shows with its country.

  A number HubSpot holds in some other style - "(541)493-2494", "541.493.2494" - is shown formatted
  but left exactly as it was until someone types in the field, so a save that did not touch the
  number does not send it back reformatted.
*/

// What usePhoneInput formats from: E.164, which a 10- or 11-digit North American number becomes.
const asInternational = (value?: string | null) => {
  const trimmed = (value ?? '').trim();
  if (!trimmed || trimmed.startsWith('+')) return trimmed;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  return trimmed;
};

interface IPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: VoidFunction;
  label: string;
  disabled?: boolean;
  errorMessage?: string;
}

function PhoneInput({ value, onChange, onBlur, label, disabled, errorMessage }: IPhoneInputProps) {
  /*
    The new number arrives through the hook's onChange - handlePhoneValueChange returns the value
    it was given, not the new one, whatever its type says (4.5.0). But the hook also calls onChange
    on its own: on mount and when the value changes from outside, with its reading of the number.
    Passing those on would reformat a number nobody touched, so only a change made inside one of
    the user's own events - typing, pasting, Ctrl+Z - reaches the form. The hook makes every call
    synchronously, inside the event that caused it.
  */
  const fromUser = useRef(false);
  const asUser = (handle: VoidFunction) => {
    fromUser.current = true;
    try {
      handle();
    } finally {
      fromUser.current = false;
    }
  };

  const { inputValue, handlePhoneValueChange, inputRef, country } = usePhoneInput({
    defaultCountry: 'us',
    value: asInternational(value),
    forceDialCode: true,
    onChange: ({ phone, country: changedTo }) => {
      if (!fromUser.current) return;
      // A field cleared back to its dial code holds no number.
      onChange(phone === `+${changedTo.dialCode}` ? '' : phone);
    },
  });

  return (
    <TextField
      type="tel"
      label={label}
      value={inputValue}
      onChange={(e) => asUser(() => handlePhoneValueChange(e))}
      // The hook's undo and redo run in its own keydown listener on the input, which fires
      // between React's capture and bubble handlers.
      onKeyDownCapture={() => {
        fromUser.current = true;
      }}
      onKeyDown={() => {
        fromUser.current = false;
      }}
      onBlur={onBlur}
      inputRef={inputRef}
      disabled={disabled}
      error={!!errorMessage}
      helperText={errorMessage}
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <FlagImage iso2={country.iso2} />
          </InputAdornment>
        ),
      }}
    />
  );
}

export default function ContactPhoneField({
  name,
  label,
  disabled,
}: {
  name: string;
  label: string;
  disabled?: boolean;
}) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <PhoneInput
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          label={label}
          disabled={disabled}
          errorMessage={error?.message}
        />
      )}
    />
  );
}
